import { Component, EventEmitter, Input, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import * as SnmpAlertActions from '@modules/snmp-custom-notifications/store/alert/actions';
import { SnmpAlertTypes } from '@modules/snmp-custom-notifications/store/alert/model';
import * as SnmpAlertSelectors from '@modules/snmp-custom-notifications/store/alert/selectors';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { I18NextPipe } from 'angular-i18next';
import { ModalService, SortEvent } from 'mbs-ui-kit';
import { Observable, combineLatest, map, noop, startWith, tap } from 'rxjs';

@UntilDestroy()
@Component({
  selector: 'mbs-snmp-custom-notifications',
  templateUrl: './snmp-custom-notifications.component.html'
})
export class SnmpCustomNotificationsComponent implements OnInit {
  @ViewChild('snmpAlertCustomisationTableTemplate', { static: true }) snmpAlertCustomisationTableTemplate: TemplateRef<any>;
  @Input() enabled = false;
  @Output() selectionChange = new EventEmitter<any>();

  public headers = [
    {
      name: 'Title',
      sort: 'name',
      gridColMin: '100px',
      gridColSize: '30fr'
    },
    {
      name: 'Device Types',
      sort: 'deviceTypeName',
      gridColMin: '100px',
      gridColSize: '10fr'
    }
  ];

  public searchForm = new FormGroup({
    searchText: new FormControl('')
  });

  public data$: Observable<(SnmpAlertTypes & { deviceTypeName: string })[]>;
  public selectedData: (SnmpAlertTypes & { deviceTypeName: string })[] = [];
  private orderBy: SortEvent = { column: 'name', direction: 'asc' };
  private selectedGuidsFromStore: string[] = [];
  public loading$: Observable<boolean>;

  constructor(private modalService: ModalService, private store: Store, private i18n: I18NextPipe) {
    this.loading$ = this.store.select(SnmpAlertSelectors.selectLoading);
    this.store.dispatch(SnmpAlertActions.loadAlertingInfo());

    const formValue$ = this.searchForm.get('searchText').valueChanges.pipe(startWith(''));
    const snmpAlertStoreValue$ = this.store.select(SnmpAlertSelectors.selectStoreData).pipe(
      tap((data) => (this.selectedGuidsFromStore = data.selectedSnmpAlertGuids)),
      map((data) =>
        data.alertTypes.map((item) => ({
          ...item,
          deviceTypeName: !item.snmp_device_type_id
            ? this.i18n.transform('mbs-ui:notifications.snmpAllTypes')
            : data.deviceTypes.find((device) => device.id === item.snmp_device_type_id)?.name
        }))
      ),
      tap((data) => (this.selectedData = data.filter((item) => this.selectedGuidsFromStore?.includes(item.alert_type_guid))))
    );

    this.data$ = combineLatest([formValue$, snmpAlertStoreValue$]).pipe(
      map(([searchValue, data]) =>
        !searchValue || searchValue === ''
          ? data
          : data.filter(
              (item) =>
                item.name.toLowerCase().includes(searchValue.toLowerCase()) ||
                item.deviceTypeName.toLowerCase().includes(searchValue.toLowerCase())
            )
      ),
      map((data: any[]) => {
        if (!data.length) return data;
        switch (this.orderBy.direction) {
          case 'asc':
            return data.sort((a, b) => (a[this.orderBy.column] > b[this.orderBy.column] ? 1 : -1));
          case 'desc':
            return data.sort((a, b) => (a[this.orderBy.column] > b[this.orderBy.column] ? -1 : 1));
          default:
            return data;
        }
      }),
      untilDestroyed(this)
    );
  }

  ngOnInit(): void {
    this.data$.pipe(untilDestroyed(this)).subscribe(noop);
  }

  handleSort(event: SortEvent) {
    this.orderBy = { ...event };
    this.searchForm.get('searchText').setValue(this.searchForm.get('searchText').value);
  }

  openSnmpAlertCustomisation() {
    this.modalService
      .open({ header: this.i18n.transform('mbs-ui:notifications.snmpAlertingTitle') }, this.snmpAlertCustomisationTableTemplate)
      .then(() => {
        this.store.dispatch(SnmpAlertActions.setSnmpAlertCustomisation({ data: this.selectedData.map((item) => item.alert_type_guid) }));
        this.selectionChange.emit(this.selectedData.map((item) => item.alert_type_guid));
      })
      .catch(() => (this.selectedData = this.selectedData.filter((item) => this.selectedGuidsFromStore.includes(item.alert_type_guid))));
  }

  handleChangeSelected(selectedData: any) {
    this.selectedData = [...selectedData];
  }
}
